import type { SupportElement } from "@/types/support";

export const formatSupportElement = (
  element: Partial<SupportElement>
): SupportElement => {
  return {
    id: element.id || 0,
    type: element.type || "product",
    support_page_id: element.support_page_id || 0,
    name: element.name || "",
    price_ht: element.price_ht || "0.00",
    price_ttc: element.price_ttc || "0.00",
    price_old: element.price_old || "0.00",
    discount_mode: element.discount_mode || 0,
    description: element.description || "",
    hook: element.hook || "",
    pictogram_id: element.pictogram_id || 0,
    product_image_id: element.product_image_id || 0,
    brand_id: element.brand_id || 0,
    element_settings: element.element_settings || {
      locked: false,
      opacity: 1,
      visible: true,
      blend_mode: "normal",
    },
    position_x: element.position_x || 0,
    position_y: element.position_y || 0,
    width: element.width || 100,
    height: element.height || 100,
    rotation: element.rotation || 0,
    z_index: element.z_index || 0,
    custom_styles: element.custom_styles || null,
  };
};

export const formatProductToElement = (product: any): SupportElement => {
  return {
    id: 0,
    type: "product",
    support_page_id: 0,
    name: product.name || "",
    price_ht: product.price?.toString() || "0.00",
    price_ttc: "0.00",
    price_old: "0.00",
    discount_mode: 0,
    description: product.description || "",
    hook: product.hook || "",
    pictogram_id: 0,
    product_image_id: product.product_image_id || 0,
    brand_id: product.brand_id || 0,
    element_settings: {
      locked: false,
      opacity: 1,
      visible: true,
      blend_mode: "normal",
    },
    position_x: 0,
    position_y: 0,
    width: 100,
    height: 100,
    rotation: 0,
    z_index: 0,
    custom_styles: null,
  };
};

/**
 * Calcule les dimensions en mm pour les supports réseaux sociaux
 * Basé sur 72 DPI (résolution standard web)
 * @param widthPx Largeur en pixels
 * @param heightPx Hauteur en pixels
 * @returns Dimensions en mm {width, height}
 */
export const calculateSocialMediaDimensions = (
  widthPx: number,
  heightPx: number
) => {
  // Conversion pixels vers mm à 72 DPI
  // 1 pouce = 25.4 mm
  // 72 pixels = 1 pouce = 25.4 mm
  // Donc 1 pixel = 25.4 / 72 = 0.352777... mm

  const mmPerPixel = 25.4 / 72;
  const widthMm = Math.round(widthPx * mmPerPixel);
  const heightMm = Math.round(heightPx * mmPerPixel);

  return { width: widthMm, height: heightMm };
};

/**
 * Mappe les formats de réseaux sociaux vers des formats acceptés par le backend
 * @returns Le format mappé pour le backend
 */
export const mapSocialMediaFormatToBackend = (): string => {
  // Pour les supports sociaux, on utilise "A4" comme format de base
  // mais les dimensions réelles sont gérées par les composants
  return "A4";
};

/**
 * Obtient les dimensions en pixels pour un réseau et format donnés
 */
export const getSocialMediaPixelDimensions = (
  network: string,
  format: string
) => {
  const dimensions: Record<
    string,
    Record<string, { width: number; height: number }>
  > = {
    instagram: {
      square: { width: 1080, height: 1080 },
      landscape: { width: 1080, height: 566 },
      portrait: { width: 1080, height: 1350 },
    },
    facebook: {
      square: { width: 1200, height: 1200 },
      landscape: { width: 1200, height: 630 },
    },
    twitter: {
      square: { width: 1080, height: 1080 },
      landscape: { width: 1080, height: 628 },
    },
    linkedin: {
      square: { width: 1080, height: 1080 },
      landscape: { width: 1080, height: 540 },
    },
  };

  return dimensions[network]?.[format] || { width: 1080, height: 1080 };
};
